import agent from 'Utils/agent';
import template from './portalPanel.component.html';
import _ from 'lodash';
import deriveIframeHost from './deriveIframeHost';
import normalizePath from './normalizePath';

/* @ngInject */
function portalPanelComponent(
  $scope,
  $element,
  $sce,
  growl,
  DashboardService,
  EquipmentInfoService
) {
  const vm = this;

  vm.showOverlay = false;
  vm.editPanel = {
    needsToBeConfigured: false
  };
  vm.shouldLoadIframeContent = false;
  vm.setSpinnerVisible = false;
  vm.isIframeContentLoaded = false;
  vm.isLoadDelayed = false;
  vm.iframeSrc = '';

  vm.headerMenu = [
    {
      text: 'Fullscreen',
      do: enterFullScreenMode
    },
    {
      text: 'Reload Content',
      do: refreshIframeContent
    }
  ];

  vm.$onInit = init;
  vm.refreshIframeContent = refreshIframeContent;
  vm.onIframeLoaded = onIframeLoaded;

  function hideIframe(hidden, overlayText) {
    vm.overlayText = overlayText;
    vm.showOverlay = hidden;
    vm.iframe.hidden = hidden;
  }

  async function init() {
    vm.currentDashboard = DashboardService.getCurrentDashboard();
    const { panels: { [vm.panelId]: panel } } = vm.currentDashboard;
    _.assign(vm, { panel });

    vm.iframe = $element.get(0).querySelector('iframe');
    vm.isLoadDelayed = panel.config.delayLoad || false;
    vm.disableCacheBuster = panel.config.disableCacheBuster || false;

    // Panel-level URL takes precedence and permits equipment-less portals.
    // SCE (resourceUrlWhitelist) gates which origins can actually load — enforced
    // in refreshIframeContent / enterFullScreenMode via $sce.getTrustedResourceUrl,
    // since the direct iframe.src assignment bypasses ng-src's implicit SCE check.
    if (panel.config.url) {
      vm.endpoint = panel.config.url + normalizePath(panel.config.path);
    } else if (_.isEmpty(panel.equipment)) {
      vm.editPanel.needsToBeConfigured = true;
      growl.warning('This dashboard contains a panel that needs to be reconfigured');
      return;
    } else {
      vm.equipmentId = vm.panel.equipment[0];
      const equipment = await EquipmentInfoService.getFullEquipment(vm.equipmentId);
      vm.objectModel = { [vm.equipmentId]: equipment };
      vm.endpoint = constructEndpointUrl(equipment);
    }

    $scope.$on(`gridster-resize-start-${vm.panelId}`, () => hideIframe(true, 'Resizing...'));
    $scope.$on(`gridster-resize-stop-${vm.panelId}`, () => hideIframe(false, 'Resizing...'));
    $scope.$on(`gridster-drag-start-${vm.panelId}`, () => hideIframe(true, 'Moving...'));
    $scope.$on(`gridster-drag-stop-${vm.panelId}`, () => hideIframe(false, 'Moving...'));

    if (!vm.isLoadDelayed) {
      refreshIframeContent();
    }
  }

  function constructEndpointUrl(equipment) {
    const {
      host,
      protocol = 'https:'
    } = window.location;
    const {
      directResource,
      resource
    } = equipment.settings;

    return (directResource || resource || `${protocol}//${equipment.id}-${deriveIframeHost(host)}`) +
      normalizePath(vm.panel.config.path);
  }

  function enterFullScreenMode() {
    const deviceSupportsFullscreen = !agent.isTouchDevice();
    if (deviceSupportsFullscreen && vm.iframe) {
      vm.iframe.requestFullscreen();
    } else {
      try {
        // This trick ensures the browser does not handle our programmatic navigation with prejudice
        // and treats it like any other click event. (vs window.open)
        Object.assign(document.createElement('a'), {
          target: '_blank',
          href: $sce.getTrustedResourceUrl(vm.endpoint)
        }).click();
      } catch {
        growl.warning('The portal URL is not allowed by security policy.');
      }
    }
  }

  function refreshIframeContent() {

    vm.setSpinnerVisible = true;
    vm.isIframeContentLoaded = false;
    // shouldLoadIframeContent is tied to the iframe ng-src and ng-show attributes
    vm.shouldLoadIframeContent = true;
    vm.iframeSrc = vm.disableCacheBuster ? vm.endpoint || '' : buildCacheBustedUrl(vm.endpoint);

    // Validate URL trust eagerly — independent of whether the iframe element is
    // in the DOM yet. If the iframe is behind an ng-if (panel in edit mode),
    // skipping this check leaves an untrusted URL in vm.iframeSrc which would
    // later trip ng-src's automatic SCE with a console-only `$sce:insecurl`
    // and no user-visible failure. Catching here lets us reset state + growl.
    if (vm.iframeSrc) {
      try {
        $sce.getTrustedResourceUrl(vm.iframeSrc);
      } catch {
        vm.setSpinnerVisible = false;
        vm.isIframeContentLoaded = false;
        vm.shouldLoadIframeContent = false;
        growl.error('The portal URL is not allowed by security policy.');
        return;
      }
    }

    if (!vm.iframe) {
      vm.iframe = $element.get(0).querySelector('iframe');
    }
    if (vm.iframe && vm.iframeSrc) {
      vm.iframe.src = vm.iframeSrc;
    }
  }

  function onIframeLoaded() {
    $scope.$applyAsync(() => {
      vm.setSpinnerVisible = false;
      vm.isIframeContentLoaded = true;
    });
  }

  function buildCacheBustedUrl(url) {
    if (!url) {
      return '';
    }

    const [baseUrl, hash] = url.split('#');
    const separator = baseUrl.includes('?') ? '&' : '?';
    const cacheBustedUrl = `${baseUrl}${separator}_ts=${Date.now()}`;

    return hash ? `${cacheBustedUrl}#${hash}` : cacheBustedUrl;
  }

}

export default {
  name: 'portalPanel',
  template,
  controller: portalPanelComponent,
  bindings: {
    panelId: '<'
  }
};
